import { create } from 'zustand';
import { DEFAULT_PAGE_SIZE } from '../config/api.config';

export interface FilterState {
  selectedCategory: string;
  selectedCommodity: string;
  selectedCountry: string;
  selectedCompany: string;
  selectedSource: string;
  dateFrom: string;
  dateTo: string;
  searchQuery: string;
  page: number;
  limit: number;
}

interface FilterActions {
  setFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void;
  setFilters: (filters: Partial<FilterState>) => void;
  resetFilters: () => void;
  setPage: (page: number) => void;
  setSearch: (query: string) => void;
}

const initialState: FilterState = {
  selectedCategory: '',
  selectedCommodity: '',
  selectedCountry: '',
  selectedCompany: '',
  selectedSource: '',
  dateFrom: '',
  dateTo: '',
  searchQuery: '',
  page: 1,
  limit: DEFAULT_PAGE_SIZE,
};

/**
 * Zustand store for managing global filter state.
 * Used across CategoryPage, Archive, and other filtered views.
 */
export const useFilterStore = create<FilterState & FilterActions>((set) => ({
  ...initialState,

  setFilter: (key, value) =>
    set((state) => ({
      ...state,
      [key]: value,
      // Reset page when any filter changes (except page itself)
      ...(key !== 'page' ? { page: 1 } : {}),
    })),

  setFilters: (filters) =>
    set((state) => ({
      ...state,
      ...filters,
      // Reset page when filters change (unless page is explicitly set)
      ...('page' in filters ? {} : { page: 1 }),
    })),

  resetFilters: () => set(initialState),

  setPage: (page) => set({ page }),

  setSearch: (query) => set({ searchQuery: query, page: 1 }),
}));

/**
 * Convenience hook that returns filter state as ArticleFilters shape.
 */
export function useFiltersAsParams() {
  const store = useFilterStore();

  return {
    category: store.selectedCategory || undefined,
    commodity: store.selectedCommodity || undefined,
    country: store.selectedCountry || undefined,
    company: store.selectedCompany || undefined,
    source: store.selectedSource || undefined,
    dateFrom: store.dateFrom || undefined,
    dateTo: store.dateTo || undefined,
    search: store.searchQuery || undefined,
    page: store.page,
    limit: store.limit,
  };
}
